import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, CalendarClock, Wallet } from "lucide-react";
import { toast } from "sonner";
import { CountUp } from "@/components/common/CountUp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

export const useInsights = () => {
  const { active } = useHousehold();
  return useQuery({ queryKey: ["insights-home", active?.id], queryFn: () => api.get("/insights/home", { params: { household_id: active?.id } }).then((r) => r.data), refetchInterval: 60000 });
};

const day = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

const AlertControl = ({ current }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(current ?? "");
  const save = async (val) => {
    try { await api.put("/insights/alert", { threshold: val === "" || val === null ? null : Number(val) }); qc.invalidateQueries({ queryKey: ["insights-home"] }); toast.success(val === null ? "Alerte désactivée" : "Alerte enregistrée"); setOpen(false); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className="mt-3 flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold hover:bg-white/20" data-testid="remain-alert-open"><BellRing className="h-3.5 w-3.5" />{current != null ? `Alerte sous ${current} € par jour` : "M'alerter sous un seuil par jour"}</button>;
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(v); }} className="mt-3 flex flex-wrap items-center gap-2" data-testid="remain-alert-form">
      <Input type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} placeholder="Seuil €/jour" className="h-9 w-32 border-white/30 bg-white/10 text-white placeholder:text-white/50" data-testid="remain-alert-input" />
      <Button type="submit" size="sm" className="rounded-full bg-[#8ce2b8] font-bold text-[#142F39] hover:bg-[#a5ebc8]" data-testid="remain-alert-save">Enregistrer</Button>
      {current != null && <Button type="button" size="sm" variant="ghost" className="rounded-full text-white hover:bg-white/10 hover:text-white" onClick={() => save(null)} data-testid="remain-alert-off">Désactiver</Button>}
    </form>
  );
};

export const RemainHero = () => {
  const { money } = useFormat();
  const { data } = useInsights();
  if (!data) return <div className="h-48 animate-pulse rounded-3xl bg-muted" />;
  const neg = data.remain < 0;
  return (
    <section className="grid gap-4 md:grid-cols-[1.3fr_1fr]" data-testid="remain-hero">
      <div className={`relative overflow-hidden rounded-3xl p-6 text-white shadow-xl ${neg ? "bg-destructive" : "bg-[#142F39]"}`}>
        <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-white/70"><Wallet className="h-4 w-4" /> Reste à vivre ce mois-ci</p>
        <div className="mt-2 text-5xl font-extrabold sm:text-6xl" data-testid="remain-amount"><CountUp value={data.remain} format={(v) => money(v, { maximumFractionDigits: 0 })} /></div>
        <p className="mt-1 text-sm font-semibold text-white/80" data-testid="remain-per-day">{neg ? "Attention : vos charges à venir dépassent ce qu'il reste ce mois-ci." : `Soit ${money(data.per_day, { maximumFractionDigits: 0 })} par jour pendant ${data.days_left} jour${data.days_left > 1 ? "s" : ""}`}</p>
        <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
          {[["Entrées", data.income, "text-[#8ce2b8]"], ["Dépensé", data.spent, "text-white"], ["À venir", data.due_month, "text-[#ffd166]"]].map(([l, v, c]) => (
            <div key={l} className="rounded-xl bg-white/10 p-2"><div className="text-white/60">{l}</div><div className={`text-base font-extrabold ${c}`}>{money(v, { maximumFractionDigits: 0 })}</div></div>))}
        </div>
        <p className="mt-3 text-[11px] text-white/50">Calculé en temps réel d'après les opérations de vos comptes et les charges récurrentes restant à payer.</p>
        <AlertControl current={data.alert?.threshold} />
      </div>
      <div className="card-surface p-5" data-testid="upcoming-charges">
        <h2 className="mb-3 flex items-center gap-2 font-extrabold"><CalendarClock className="h-5 w-5 text-decision" /> Charges à venir</h2>
        {data.upcoming.length ? <ul className="divide-y divide-border text-sm">{data.upcoming.map((u, i) => (
          <li key={`${u.name}-${i}`} className="flex items-center justify-between gap-2 py-2" data-testid={`upcoming-${i}`}><span className="min-w-0 truncate"><span className="mr-2 inline-block w-14 text-xs font-bold text-muted-foreground">{day(u.date)}</span>{u.name}</span><span className="font-bold">−{money(u.amount)}</span></li>))}</ul>
          : <p className="text-sm text-muted-foreground">Aucune charge récurrente prévue dans les 30 prochains jours.</p>}
      </div>
    </section>
  );
};
